import { ManifoldWindow } from 'build/mount';
import { mixins } from 'vue-class-component';
import { setProviderEventsHandler } from '@manifoldxyz/manifold-dropsitetools-lib';
// import { EthereumNetwork, EthereumProvider, OAuthGrantType } from '@manifoldxyz/manifold-sdk';
import { EthereumProvider } from '@manifoldxyz/manifold-sdk';
import { RedemptionResult } from '@/api/models';
import RedeemServerClient from '@/api/redeem/redeem';
import ShopifyClient from '@/api/shopify/shopify';
import { ShopifyCart } from '@/api/shopify/ShopifyCart';
import { TokenCart } from '@/api/shopify/TokenCart';
import { CAMPAIGN_STORE } from '@/constants/campaign';
import { MCartWidgetInjectContext } from '@/exports/MCartWidgetProps';
import store from '@/store';

export default class CartWidgetMixin extends mixins(MCartWidgetInjectContext) {
  // Important to note - the async calls in here are not resolved
  // before the component is mounted. So state is not always defined.
  async created() {
    try {
      const shopifyClient = new ShopifyClient();

      const currentShop = await shopifyClient.getCurrentShop();

      let currentProductId;

      if (this.productIdOverride) {
        currentProductId = this.productIdOverride;
      } else {
        // Get current product, will be used to initialize the store
        const currentProduct = await shopifyClient.getCurrentProduct();
        currentProductId = currentProduct.id ? currentProduct.id : currentProduct.product.id;
      }

      this.mmid = `${currentProductId}`;

      // SET UP STORE FOR THIS CAMPAIGN
      const redeemAPIClient = new RedeemServerClient();
      this.$store = (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
      if (!this.$store.state[this.mmid]) {
        this.$store.registerModule(`${this.mmid}`, store);
        this.$store.commit(`${this.mmid}/setProductId`, parseInt(this.mmid), { root: true });
        this.$store.commit(`${this.mmid}/setShopifyAPIClient`, shopifyClient, { root: true });
        this.$store.commit(`${this.mmid}/setRedeemAPIClient`, redeemAPIClient, { root: true });

        if (this.clientId) {
          this.$store.commit(`${this.mmid}/setClientId`, this.clientId, { root: true });
        }
        if (this.appName) {
          this.$store.commit(`${this.mmid}/setAppName`, this.appName, { root: true });
        }
        if (this.grantType) {
          this.$store.commit(`${this.mmid}/setGrantType`, this.grantType, { root: true });
        }
      }

      this.$watch('state.leadingComponent', this.onLeadingComponent);
      this.registerLeadingComponent();

      if (this.state && this.state.leadingComponent === this) {
        // Should be done before even connected
        const isUnrestricted = await redeemAPIClient.getProductIsUnrestricted(
          currentShop,
          currentProductId
        );
        this.$store.commit(`${this.mmid}/setIsUnrestricted`, isUnrestricted, { root: true });

        setProviderEventsHandler(this.initialize);
        window.addEventListener('m-authenticated', this.initialize);

        const provider = EthereumProvider.provider();
        if (!provider) {
          // No provider connected yet, so attempt to initialize connection, default to mainnet
          await EthereumProvider.initialize(this.network);
        }

        await this.initialize();
      }
    } catch (e) {
      console.error(`Problem loading promo for product ${this.mmid}`, e);
    } finally {
      this.loading = false;
    }
  }

  interval() {
    setTimeout(() => {
      this.$store.dispatch(`${this.mmid}/updateCountdown`, { root: true });
      requestAnimationFrame(this.interval);
    }, 1000);
  }

  onLeadingComponent(value: any, _oldValue: any) {
    if (!value) {
      this.registerLeadingComponent();
    }
  }

  beforeUnmount() {
    if (this.state && this.state.leadingComponent === this) {
      this.$store.commit(`${this.mmid}/setLeadingComponentForMMID`, undefined, { root: true });
    }
  }

  registerLeadingComponent() {
    if (!this.state || !this.state.leadingComponent) {
      this.$store.commit(`${this.mmid}/setLeadingComponentForMMID`, this, { root: true });
    }
  }

  async initialize() {
    if (this.state.leadingComponent === this) {
      const handling = this.state.isHandlingProviderEvent;

      const connectedAddress = EthereumProvider.selectedAddress();
      // Process if we are not already handling or the address being handled has changed
      if (!handling.status || handling.address != connectedAddress) {
        this.$store.commit(
          `${this.mmid}/setIsHandlingProviderEvent`,
          { status: true, address: connectedAddress },
          { root: true }
        );
        this.$store.commit(`${this.mmid}/setWrongChain`, !EthereumProvider.chainIsCorrect(), {
          root: true
        });

        try {
          await this.updateState();
        } catch (e) {
          console.error(e);
        }

        this.$store.commit(
          `${this.mmid}/setIsHandlingProviderEvent`,
          { status: false, address: connectedAddress },
          { root: true }
        );
      }
    }
  }

  async updateState(force = false) {
    if (force || this.state.leadingComponent === this) {
      // Start loading
      if (force) {
        this.$store.commit(`${this.mmid}/setLoaded`, false, { root: true });
      }
      const loading = this.state.loading;
      this.$store.commit(`${this.mmid}/setLoading`, true, { root: true });
      try {
        // -------------------------
        // Load product page details
        // -------------------------
        let currentProductId;
        const currentShop = this.state.ShopifyAPIClient.getCurrentShop();
        if (this.productIdOverride) {
          currentProductId = this.productIdOverride;
        } else {
          const currentProduct = await this.state.ShopifyAPIClient.getCurrentProduct();
          currentProductId = currentProduct.id ? currentProduct.id : currentProduct.product.id;
        }
        // -----------------------------
        // End Load product page details
        // -----------------------------

        const connectedAddress = EthereumProvider.selectedAddress();
        let oauthToken;
        if (connectedAddress) {
          oauthToken = await this.getOAuth(true);
        }

        // Detect if wallet address has changed and set the wallet address to prevent race conditions
        const walletAddressChanged = this.state.walletAddress !== connectedAddress;
        this.$store.commit(
          `${this.mmid}/setWalletAddress`,
          !!oauthToken ? connectedAddress : undefined,
          { root: true }
        );

        if (walletAddressChanged) {
          this.clearState();
        }

        // -----------------------------
        // Load product campaign details
        // -----------------------------
        if (!this.state.campaign && !loading) {
          // Only need to load if not yet loaded or another thread is not loading the campaign data
          // Get campaigns for current product
          const campaignForCurrentProduct = await this.state.RedeemAPIClient.getCampaignForProduct(
            currentShop,
            currentProductId
          );

          const campaign = campaignForCurrentProduct.campaign;
          this.$store.commit(`${this.mmid}/setCampaign`, campaign, {
            root: true
          });
          this.$store.commit(`${this.mmid}/setCampaignProduct`, campaignForCurrentProduct, {
            root: true
          });
          this.$store.commit(`${this.mmid}/setCampaignStartTime`, campaign.startDate, {
            root: true
          });
          this.$store.commit(`${this.mmid}/setCampaignEndTime`, campaign.endDate, { root: true });
          this.$store.commit(
            `${this.mmid}/setCampaignIsSoldOut`,
            campaignForCurrentProduct?.total && campaignForCurrentProduct?.consumed
              ? campaignForCurrentProduct.total !== 0 &&
                  campaignForCurrentProduct.total - campaignForCurrentProduct.consumed < 1
              : false,
            { root: true }
          );
          requestAnimationFrame(this.interval);
        }

        // ---------------------------------
        // End Load product campaign details
        // ---------------------------------

        if (!EthereumProvider.selectedAddress() || !oauthToken) {
          // No other info to load if they are not logged in
          return;
        }

        // ---------------
        // Load Token Cart
        // ---------------
        let storedShopifyCart = undefined;
        // Re-retrieve the token cart from server if we are connected and the wallet address has changed
        if (oauthToken && walletAddressChanged) {
          const currentShop = await this.state.ShopifyAPIClient.getCurrentShop();
          // Get cart from Redeem
          storedShopifyCart = await this.state.RedeemAPIClient.getCart(oauthToken, currentShop);
        }

        // Clobber the existing token cart items in the browser cart if the wallet address has changed
        if (walletAddressChanged) {
          const shopifyCart = new ShopifyCart(await this.state.ShopifyAPIClient.getBrowserCart());
          storedShopifyCart = await this.syncShopifyCart(
            oauthToken,
            currentShop,
            shopifyCart,
            storedShopifyCart
          );
          this.$store.commit(`${this.mmid}/setStoredShopifyCart`, storedShopifyCart, {
            root: true
          });
        }

        // -------------------
        // End Load Token Cart
        // -------------------

        // -----------------------------
        // Load eligible rewards details
        // -----------------------------

        if ((!this.state.userInfoLoaded || walletAddressChanged) && this.getters.campaignIsActive) {
          const eligibleRewards = await this.state.RedeemAPIClient.getEligibleAwardsForProduct(
            oauthToken,
            currentShop,
            currentProductId
          );

          this.$store.commit(`${this.mmid}/setUserInfoLoaded`, true, { root: true });
          try {
            for (const reward of eligibleRewards) {
              reward.token.image = reward.token.image?.replace(
                'ipfs://ipfs/',
                'https://ipfs.io/ipfs/'
              );
              reward.token.image = reward.token.image?.replace('ipfs://', 'https://ipfs.io/ipfs/');
            }
            // Get all eligible rewards for the current product -- will filter out already redeemed rewards later
            this.$store.commit(`${this.mmid}/setEligibleRewards`, eligibleRewards, { root: true });
            const redemptions = await this.state.RedeemAPIClient.getRedemptions(
              oauthToken,
              this.state.campaign.id
            );
            let redemptionsByOthers: RedemptionResult[] = [];
            if (!this.state.campaign.snapshot) {
              redemptionsByOthers = await this.state.RedeemAPIClient.getRedemptionsByOthers(
                oauthToken,
                this.state.campaign.id!
              );
            }
            for (const redemption of redemptions) {
              redemption.image = redemption.image?.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
              redemption.image = redemption.image?.replace('ipfs://', 'https://ipfs.io/ipfs/');
            }
            for (const redemption of redemptionsByOthers) {
              redemption.image = redemption.image?.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
              redemption.image = redemption.image?.replace('ipfs://', 'https://ipfs.io/ipfs/');
            }
            this.$store.commit(`${this.mmid}/setAlreadyRedeemed`, redemptions, { root: true });
            this.$store.commit(`${this.mmid}/setAlreadyRedeemedByOthers`, redemptionsByOthers, {
              root: true
            });
          } catch (error) {
            // Error, unload user info
            this.clearState();
          }
        }

        // ----------------------------------
        // Done load eligible rewards details
        // ----------------------------------
      } finally {
        this.$store.commit(`${this.mmid}/setLoaded`, true, { root: true });
        this.$store.commit(`${this.mmid}/setLoading`, false, { root: true });
      }
    }
  }

  /**
   * Helper function to clear state
   */
  async clearState() {
    this.$store.commit(`${this.mmid}/setLoaded`, false, { root: true });
    this.$store.commit(`${this.mmid}/setStoredShopifyCart`, undefined, { root: true });
    this.$store.commit(`${this.mmid}/setEligibleRewards`, [], { root: true });
    this.$store.commit(`${this.mmid}/setTokensForCampaign`, [], { root: true });
    this.$store.commit(`${this.mmid}/setAlreadyRedeemed`, [], { root: true });
    this.$store.commit(`${this.mmid}/setUserInfoLoaded`, false, { root: true });
  }

  /**
   * Helper function to sync shopify cart with stored cart
   */
  async syncShopifyCart(
    oauthToken: string,
    currentShop: string,
    shopifyCart: ShopifyCart,
    storedShopifyCart: TokenCart
  ): Promise<TokenCart> {
    const resp = await shopifyCart.initialSyncWithTokenCart(
      storedShopifyCart,
      this.cartIconSection
    );
    if (resp && resp.items.length === 0 && storedShopifyCart.items.length > 0) {
      // The shopify cart was cleared due to an error
      storedShopifyCart = await this.state.RedeemAPIClient.clearCart(oauthToken, currentShop);
    }

    if (resp && resp.sections && this.cartIconSection) {
      for (const cartIconSection of this.cartIconSection.split(',')) {
        const newCartIconBubble = resp.sections[cartIconSection];
        if (document.getElementById(cartIconSection) && newCartIconBubble) {
          document.getElementById(cartIconSection)!.innerHTML = newCartIconBubble;
        }
      }
    }
    return storedShopifyCart;
  }

  getOAuth(delayAuth = false) {
    return EthereumProvider.getOAuth({
      grantType: this.state.grantType,
      appName: this.state.appName,
      clientId: this.state.clientId,
      strictAuth: true,
      delayAuth: delayAuth
    });
  }

  get state() {
    return this.$store.state[this.mmid];
  }

  get getters() {
    return {
      walletConnected: this.$store.getters[`${this.mmid}/walletConnected`],
      campaignIsActive: this.$store.getters[`${this.mmid}/campaignIsActive`],
      countdown: this.$store.getters[`${this.mmid}/countdown`],
      countdownType: this.$store.getters[`${this.mmid}/countdownType`],
      productHasCampaign: this.$store.getters[`${this.mmid}/productHasCampaign`]
    };
  }
}
